/**
 * LabCategoryDocumentsTab.tsx
 *
 * Standalone patient-sidebar section for a single lab-document category
 * (Imaging / Genetics). Renders the same list UI as the Labs > Documents tab
 * (DocumentsSubTab in uploadsOnly mode) but fed by a category-specific endpoint.
 *
 * Uploads from this tab are pre-categorized: the Upload button opens
 * LabUploadModal with `forceFileType`, which stamps notes_attachments.file_type
 * directly so the doc lands in this category without waiting on (or being
 * reclassified by) the legacy classifier (note_attachment_process_autorun.php).
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Printer, Upload } from 'lucide-react';
import { DocumentsSubTab } from './DocumentsSubTab';
import { LabUploadModal } from './LabUploadModal';
import type { LabPreviewTarget } from './LabPdfPreviewPanel';
import type { LabDocument } from '@/services/labs.service';
import { Button } from '@/components/ui/Button';

interface LabCategoryDocumentsTabProps {
  patientId: number;
  title: string;
  fileType: 'IMAGING' | 'GENETICS';
  documentsFetcher: (patientId: number, includeDeleted: boolean) => Promise<LabDocument[]>;
  documentsQueryKey: string;
  emptyMessage: string;
  onPreview: (target: LabPreviewTarget) => void;
}

export function LabCategoryDocumentsTab({
  patientId,
  title,
  fileType,
  documentsFetcher,
  documentsQueryKey,
  emptyMessage,
  onPreview,
}: LabCategoryDocumentsTabProps) {
  const queryClient = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);

  function handleRefresh() {
    void queryClient.invalidateQueries({ queryKey: [documentsQueryKey, patientId] });
  }

  return (
    <div className="space-y-4 py-2">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="secondary" onClick={handleRefresh}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            Refresh
          </Button>
          <Button size="sm" variant="secondary" onClick={() => window.print()}>
            <Printer className="h-3.5 w-3.5 mr-1" />
            Print
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setUploadOpen(true)}>
            <Upload className="h-3.5 w-3.5 mr-1" />
            Upload
          </Button>
        </div>
      </div>
      <DocumentsSubTab
        patientId={patientId}
        results={[]}
        resultsLoading={false}
        onPreview={onPreview}
        uploadsOnly
        documentsFetcher={documentsFetcher}
        documentsQueryKey={documentsQueryKey}
        emptyMessage={emptyMessage}
        allowShowDeleted
      />
      <LabUploadModal
        patientId={patientId}
        open={uploadOpen}
        forceFileType={fileType}
        onClose={() => setUploadOpen(false)}
        onUploaded={() => {
          setUploadOpen(false);
          handleRefresh();
        }}
      />
    </div>
  );
}
